import { useMutation } from "@apollo/client";
import noop from "lodash/noop";
import DestroyPostMutation from "lib/graphql/mutations/engagement/DestroyPostMutation";
import type {
  IDestroyPostMutation,
  IDestroyPostMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/DestroyPostMutation.generated";
import type {
  IEditPostMutation,
  IEditPostMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/EditPostMutation.generated";
import EditPostMutation from "lib/graphql/mutations/engagement/EditPostMutation";
import type {
  ILockPostMutation,
  ILockPostMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/LockPostMutation.generated";
import LockPostMutation from "lib/graphql/mutations/engagement/LockPostMutation";
import UnlockPostMutation from "lib/graphql/mutations/engagement/UnlockPostMutation";
import PinPostMutation from "lib/graphql/mutations/engagement/PinPostMutation";
import UnpinPostMutation from "lib/graphql/mutations/engagement/UnpinPostMutation";
import type {
  IPinPostMutation,
  IPinPostMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/PinPostMutation.generated";
import type {
  IEditPostReplyMutation,
  IEditPostReplyMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/EditPostReplyMutation.generated";
import EditPostReplyMutation from "lib/graphql/mutations/engagement/EditPostReplyMutation";
import type {
  IDestroyPostReplyMutation,
  IDestroyPostReplyMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/DestroyPostReplyMutation.generated";
import DestroyPostReplyMutation from "lib/graphql/mutations/engagement/DestroyPostReplyMutation";
import type {
  IPinPostReplyMutation,
  IPinPostReplyMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/PinPostReplyMutation.generated";
import PinPostReplyMutation from "lib/graphql/mutations/engagement/PinPostReplyMutation";
import type {
  IUnpinPostReplyMutation,
  IUnpinPostReplyMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/UnpinPostReplyMutation.generated";
import UnpinPostReplyMutation from "lib/graphql/mutations/engagement/UnpinPostReplyMutation";
import type {
  IUnlockPostMutation,
  IUnlockPostMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/UnlockPostMutation.generated";
import type {
  IUnpinPostMutation,
  IUnpinPostMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/UnpinPostMutation.generated";
import type {
  ISetPostReplyAnswerMutation,
  ISetPostReplyAnswerMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/SetPostReplyAnswerMutation.generated";
import SetPostReplyAnswerMutation from "lib/graphql/mutations/engagement/SetPostReplyAnswerMutation";
import type {
  IUnsetPostReplyAnswerMutation,
  IUnsetPostReplyAnswerMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/UnsetPostReplyAnswerMutation.generated";
import UnsetPostReplyAnswerMutation from "lib/graphql/mutations/engagement/UnsetPostReplyAnswerMutation";

export default function usePostActions() {
  const [destroyPost, { loading: isDestroyingPost }] = useMutation<IDestroyPostMutation, IDestroyPostMutationVariables>(
    DestroyPostMutation,
    {
      onError: noop,
    },
  );

  const [destroyPostReply, { loading: isDestroyingPostReply }] = useMutation<
    IDestroyPostReplyMutation,
    IDestroyPostReplyMutationVariables
  >(DestroyPostReplyMutation, {
    onError: noop,
  });

  const [editPost, { loading: isEditingPost }] = useMutation<IEditPostMutation, IEditPostMutationVariables>(
    EditPostMutation,
    {
      onError: noop,
    },
  );

  const [editPostReply, { loading: isEditingPostReply }] = useMutation<
    IEditPostReplyMutation,
    IEditPostReplyMutationVariables
  >(EditPostReplyMutation, {
    onError: noop,
  });

  const [lockPost, { loading: isLockingPost }] = useMutation<ILockPostMutation, ILockPostMutationVariables>(
    LockPostMutation,
    {
      onError: noop,
    },
  );

  const [unlockPost, { loading: isUnlockingPost }] = useMutation<IUnlockPostMutation, IUnlockPostMutationVariables>(
    UnlockPostMutation,
    {
      onError: noop,
    },
  );

  const [pinPost, { loading: isPinningPost }] = useMutation<IPinPostMutation, IPinPostMutationVariables>(
    PinPostMutation,
    {
      onError: noop,
    },
  );

  const [unpinPost, { loading: isUnpinningPost }] = useMutation<IUnpinPostMutation, IUnpinPostMutationVariables>(
    UnpinPostMutation,
    {
      onError: noop,
    },
  );

  const [pinPostReply, { loading: isPinningPostReply }] = useMutation<
    IPinPostReplyMutation,
    IPinPostReplyMutationVariables
  >(PinPostReplyMutation, {
    onError: noop,
  });

  const [unpinPostReply, { loading: isUnpinningPostReply }] = useMutation<
    IUnpinPostReplyMutation,
    IUnpinPostReplyMutationVariables
  >(UnpinPostReplyMutation, {
    onError: noop,
  });

  const [setPostReplyAnswer, { loading: isSettingAnswerPostReply }] = useMutation<
    ISetPostReplyAnswerMutation,
    ISetPostReplyAnswerMutationVariables
  >(SetPostReplyAnswerMutation, {
    onError: noop,
  });

  const [unsetPostReplyAnswer, { loading: isUnsettingAnswerPostReply }] = useMutation<
    IUnsetPostReplyAnswerMutation,
    IUnsetPostReplyAnswerMutationVariables
  >(UnsetPostReplyAnswerMutation, {
    onError: noop,
  });

  return {
    destroyPost,
    isDestroyingPost,

    destroyPostReply,
    isDestroyingPostReply,

    editPost,
    isEditingPost,

    editPostReply,
    isEditingPostReply,

    lockPost,
    isLockingPost,

    unlockPost,
    isUnlockingPost,

    pinPost,
    isPinningPost,

    unpinPost,
    isUnpinningPost,

    pinPostReply,
    isPinningPostReply,

    unpinPostReply,
    isUnpinningPostReply,

    setPostReplyAnswer,
    isSettingAnswerPostReply,

    unsetPostReplyAnswer,
    isUnsettingAnswerPostReply,
  };
}
